import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Fade,
  Paper,
  Typography,
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ShieldIcon from "@mui/icons-material/Shield";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SpeedIcon from "@mui/icons-material/Speed";
import FlagIcon from "@mui/icons-material/Flag";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import PaymentIcon from "@mui/icons-material/Payment";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DescriptionIcon from "@mui/icons-material/Description";

// ─── HELPERS ────────────────────────────────────────────────────────────────
const getToken = () => {
  let token = localStorage.getItem("token") || "";
  if (token.startsWith('"') && token.endsWith('"')) {
    token = token.slice(1, -1);
  }
  return token;
};

const apiFetch = async (url, opts = {}) => {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(url, { ...opts, headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
};

const fmt = (n) => n?.toLocaleString("en-KE", { minimumFractionDigits: 0 }) ?? "—";
const API_BASE = "";

// ─── PARSE CREDIT RECOMMENDATION ────────────────────────────────────────────
const parseCreditRecommendation = (cr) => {
  if (!cr) return null;
  if (typeof cr === "string") {
    try {
      return JSON.parse(cr);
    } catch (e) {
      console.error("Failed to parse credit_recommendation:", e);
      return null;
    }
  }
  return cr;
};

// ─── THEME-AWARE SUB-COMPONENTS ─────────────────────────────────────────────
// (these receive theme via useTheme inside NarrativeReportPage, but we'll pass theme as argument or use hook inside)
// To keep code clean, we'll define them inside the main component so they have access to theme.

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
const NarrativeReportPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [structuredData, setStructuredData] = useState(null);
  const [rawText, setRawText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [animatedScore, setAnimatedScore] = useState(0);

  const statementData = JSON.parse(localStorage.getItem("statementData") || "{}");
  const statementId = statementData?.id || statementData?.statementid || statementData?.statement_id;

  // Theme-aware color helpers (used inside component)
  const riskColor = (level) => {
    if (!level) return theme.palette.text.secondary;
    const l = level.toLowerCase();
    if (l === "low") return theme.palette.success.main;
    if (l === "medium") return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const riskBg = (level) => {
    if (!level) return alpha(theme.palette.text.secondary, 0.08);
    const l = level.toLowerCase();
    if (l === "low") return alpha(theme.palette.success.main, 0.1);
    if (l === "medium") return alpha(theme.palette.warning.main, 0.1);
    return alpha(theme.palette.error.main, 0.1);
  };

  const scoreColor = (score) => {
    if (score >= 700) return theme.palette.success.main;
    if (score >= 550) return theme.palette.warning.main;
    if (score >= 400) return theme.palette.error.light;
    return theme.palette.error.main;
  };

  const scoreLabel = (score) => {
    if (score >= 700) return "Excellent";
    if (score >= 600) return "Good";
    if (score >= 500) return "Fair";
    if (score >= 400) return "Poor";
    return "Very Poor";
  };

  useEffect(() => {
    if (!statementId) {
      setError("No statement selected. Please select a statement from the table.");
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const result = await apiFetch(`${API_BASE}/api/narrative/${statementId}/report`);
        // possible shapes: { result: { narrative: {...} } } or { result: { narrative: "text..." } } or { content: "...", format: "text" }
        let narrative = result?.result?.narrative || result?.narrative || result;

        // Check if it's a plain text report (either string or object with content field)
        if (typeof narrative === "string") {
          setRawText(narrative);
          setStructuredData(null);
        } else if (narrative && typeof narrative === "object") {
          if (narrative.content && narrative.format === "text") {
            // It's the raw text report wrapped in an object
            setRawText(narrative.content);
            setStructuredData(null);
          } else {
            // Structured JSON narrative
            setStructuredData(narrative);
            setRawText(null);
            // Animate score if present
            const targetScore = narrative?.credit_score || 0;
            const duration = 1500;
            const startTime = Date.now();
            const animate = () => {
              const elapsed = Date.now() - startTime;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setAnimatedScore(Math.round(targetScore * eased));
              if (progress < 1) requestAnimationFrame(animate);
            };
            setTimeout(() => requestAnimationFrame(animate), 300);
          }
        } else {
          setError("Unexpected response format from server.");
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [statementId, theme]);

  // Build tabs based on available data
  const getTabs = () => {
    const baseTabs = [];
    if (structuredData) {
      baseTabs.push(
        { key: "overview", label: "Overview", icon: AssessmentIcon },
        { key: "income", label: "Income", icon: TrendingUpIcon },
        { key: "spend", label: "Spending", icon: ShoppingCartIcon },
        { key: "debt", label: "Debt", icon: CreditCardIcon },
        { key: "recommendation", label: "Recommendation", icon: MonetizationOnIcon }
      );
    }
    if (rawText) {
      baseTabs.push({ key: "raw", label: "Full Text Report", icon: DescriptionIcon });
    }
    return baseTabs;
  };

  const tabs = getTabs();
  const n = structuredData;
  const cr = parseCreditRecommendation(n?.credit_recommendation);

  const handleBack = () => navigate(-1);

  // ─── THEME-AWARE SCORE GAUGE ──────────────────────────────────────────────
  const ScoreGauge = ({ score = 0 }) => {
    const pct = Math.min(Math.max(score / 850, 0), 1);
    const color = scoreColor(score);
    const label = scoreLabel(score);

    const size = 180;
    const strokeWidth = 14;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const arcLength = circumference * 0.75;
    const offset = circumference - pct * arcLength;

    return (
      <Box sx={{ position: "relative", width: size, height: size * 0.75, mx: "auto" }}>
        <svg width={size} height={size * 0.75} viewBox={`0 0 ${size} ${size * 0.75}`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={theme.palette.divider}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${arcLength} ${circumference}`}
            transform={`rotate(135 ${size / 2} ${size / 2})`}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeDashoffset={offset}
            transform={`rotate(135 ${size / 2} ${size / 2})`}
            style={{ transition: "stroke-dashoffset 1s ease-out" }}
          />
        </svg>
        <Box
          sx={{
            position: "absolute",
            top: "55%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, color, lineHeight: 1 }}>
            {score || "—"}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            / 850
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Chip
              label={label}
              size="small"
              sx={{
                backgroundColor: alpha(color, 0.12),
                color: color,
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  // ─── SCORE BREAKDOWN BAR (theme-aware) ────────────────────────────────────
  const ScoreBreakdownBar = ({ breakdown = {} }) => {
    const entries = Object.entries(breakdown);
    const maxVal = Math.max(...entries.map(([, v]) => Math.abs(v)), 1);
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {entries.map(([key, value]) => {
          const isPositive = value >= 0;
          const barWidth = (Math.abs(value) / maxVal) * 100;
          return (
            <Box key={key}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textTransform: "capitalize" }}>
                  {key.replace(/_/g, " ").replace(/ component/g, "")}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: isPositive ? theme.palette.success.main : theme.palette.error.main }}>
                  {isPositive ? "+" : ""}{value} pts
                </Typography>
              </Box>
              <Box sx={{ height: 8, backgroundColor: alpha(theme.palette.grey[500], 0.2), borderRadius: 4, overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${barWidth}%`,
                    backgroundColor: isPositive ? theme.palette.success.main : theme.palette.error.main,
                    borderRadius: 4,
                    transition: "width 1s ease-out",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ─── SPEND BAR CHART (theme-aware colors) ─────────────────────────────────
  const SpendBarChart = ({ categories = [] }) => {
    const max = Math.max(...categories.map((c) => c.monthly_avg || 0), 1);
    const colorPalette = [
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.grey[600],
    ];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {categories.map((cat, i) => (
          <Box key={i}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: colorPalette[i % colorPalette.length],
                  }}
                />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {cat.category}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                KSh {fmt(cat.monthly_avg)}{" "}
                <Typography component="span" variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 400 }}>
                  ({cat.percentage?.toFixed(1)}%)
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ height: 8, backgroundColor: alpha(theme.palette.grey[500], 0.2), borderRadius: 4, overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${(cat.monthly_avg / max) * 100}%`,
                  backgroundColor: colorPalette[i % colorPalette.length],
                  borderRadius: 4,
                  transition: "width 0.8s ease-out",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // ─── METRIC CARD (theme-aware) ────────────────────────────────────────────
  const MetricCard = ({ icon: Icon, label, value, subtext, color = "primary" }) => {
    const colorMap = {
      primary: theme.palette.primary.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      info: theme.palette.info.main,
    };
    const mainColor = colorMap[color] || theme.palette.primary.main;
    return (
      <Card
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[2],
          },
        }}
      >
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: alpha(mainColor, 0.12),
                color: mainColor,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {label}
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
            {value}
          </Typography>
          {subtext && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {subtext}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  // ─── RISK BADGE (theme-aware) ─────────────────────────────────────────────
  const RiskBadge = ({ level }) => {
    const color = riskColor(level);
    const bg = riskBg(level);
    return (
      <Chip
        icon={<ShieldIcon sx={{ fontSize: 16, color: `${color} !important` }} />}
        label={`${level || "Unknown"} Risk`}
        sx={{
          backgroundColor: bg,
          color: color,
          fontWeight: 700,
          fontSize: "0.875rem",
          px: 1,
          py: 0.5,
          border: `1.5px solid ${alpha(color, 0.2)}`,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  };

  // ─── FLAG ITEM (theme-aware) ──────────────────────────────────────────────
  const FlagItem = ({ text, type = "risk" }) => {
    const isRisk = type === "risk";
    const mainColor = isRisk ? theme.palette.error.main : theme.palette.success.main;
    const bgColor = alpha(mainColor, 0.1);
    const borderColor = alpha(mainColor, 0.25);
    return (
      <Fade in timeout={500}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            p: 1.5,
            borderRadius: 2,
            backgroundColor: bgColor,
            border: `1px solid ${borderColor}`,
          }}
        >
          {isRisk ? (
            <WarningAmberIcon sx={{ fontSize: 18, color: theme.palette.error.main, mt: 0.3, flexShrink: 0 }} />
          ) : (
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: theme.palette.success.main, mt: 0.3, flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ color: isRisk ? theme.palette.error.dark : theme.palette.success.dark, lineHeight: 1.5 }}>
            {text}
          </Typography>
        </Box>
      </Fade>
    );
  };

  // ─── INSTALLMENT OPTION CARD (theme-aware) ────────────────────────────────
  const InstallmentCard = ({ opt, index, isRecommended }) => (
    <Card
      elevation={0}
      sx={{
        border: `1.5px solid ${isRecommended ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: 3,
        backgroundColor: isRecommended ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
        position: "relative",
        overflow: "visible",
        transition: "all 0.2s",
        "&:hover": {
          boxShadow: theme.shadows[2],
        },
      }}
    >
      {isRecommended && (
        <Chip
          label="RECOMMENDED"
          size="small"
          color="primary"
          sx={{
            position: "absolute",
            top: -12,
            left: 16,
            fontWeight: 700,
            fontSize: "0.65rem",
            letterSpacing: 0.5,
          }}
        />
      )}
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 0.5 }}>
              Term
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              {opt.term_label}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 0.5 }}>
              Loan Amount
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              KSh {fmt(opt.loan_amount)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 0.5 }}>
              Weekly Payment
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              KSh {fmt(opt.weekly_installment)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", mb: 0.5 }}>
              Total Repayable
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              KSh {fmt(opt.total_repayable)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // ─── SECTION HEADER (theme-aware) ─────────────────────────────────────────
  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Avatar sx={{ width: 32, height: 32, backgroundColor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
          <Icon sx={{ fontSize: 18 }} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, ml: 5.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: theme.palette.background.default, minHeight: "100vh" }}>
      {/* Back Button */}
      <Box mb={2}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 500,
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          Back to Statements
        </Button>
      </Box>

      {/* Main Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: 3,
            pb: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: "sticky",
            top: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography variant="overline" sx={{ color: theme.palette.text.secondary, letterSpacing: 1.5, fontWeight: 600 }}>
                Credit Report
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary, mt: 0.5, fontSize: { xs: "1.5rem", md: "2rem" } }}>
                {loading ? "Loading…" : (structuredData?.customer_name || rawText ? "Credit Report" : `Statement #${statementId}`)}
              </Typography>
              {structuredData?.statement_period && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14 }} />
                  {structuredData.statement_period}
                </Typography>
              )}
            </Box>
            {structuredData && cr?.risk_level && (
              <Box sx={{ mt: 1 }}>
                <RiskBadge level={cr.risk_level} />
              </Box>
            )}
          </Box>

          {/* Tabs */}
          {tabs.length > 0 && (
            <Box sx={{ display: "flex", gap: 0, mt: 1, overflowX: "auto" }}>
              {tabs.map((t) => {
                const isActive = tab === t.key;
                const TabIcon = t.icon;
                return (
                  <Button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    startIcon={<TabIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderRadius: 0,
                      borderBottom: "3px solid",
                      borderColor: isActive ? theme.palette.primary.main : "transparent",
                      color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: "0.875rem",
                      textTransform: "none",
                      minWidth: "auto",
                      whiteSpace: "nowrap",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.action.hover, 0.08),
                        color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
                      },
                    }}
                  >
                    {t.label}
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "60vh" }}>
          {loading && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 300, gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderTopColor: theme.palette.primary.main,
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                }}
              />
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Generating credit narrative…
              </Typography>
            </Box>
          )}

          {error && (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.08),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 3,
                p: 2.5,
                color: theme.palette.error.dark,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <WarningAmberIcon />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {error}
              </Typography>
            </Paper>
          )}

          {/* STRUCTURED DATA VIEW */}
          {!loading && !error && structuredData && (
            <>
              {tab === "overview" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={5}>
                      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 2, height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: theme.palette.text.primary, display: "flex", alignItems: "center", gap: 1 }}>
                          <SpeedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          Credit Score
                        </Typography>
                        <ScoreGauge score={animatedScore} />
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 2.5, height: "100%" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: theme.palette.text.primary, display: "flex", alignItems: "center", gap: 1 }}>
                          <BarChartIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          Score Breakdown
                        </Typography>
                        <ScoreBreakdownBar breakdown={structuredData.credit_score_breakdown || {}} />
                      </Card>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={CalendarTodayIcon} label="Months Analyzed" value={structuredData.months_analyzed ?? "—"} color="info" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={TrendingUpIcon} label="Avg Monthly Inflow" value={`KSh ${fmt(structuredData.income?.monthly_average)}`} color="success" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={ShoppingCartIcon} label="Avg Monthly Spend" value={`KSh ${fmt(structuredData.spending?.total_monthly_average)}`} color="warning" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={AccountBalanceWalletIcon} label="Balance Trend" value={structuredData.balance_trend || "—"} subtext="Over analysis period" color="primary" />
                    </Grid>
                  </Grid>

                  {structuredData.risk_flags?.length > 0 && (
                    <Box>
                      <SectionHeader icon={FlagIcon} title="Risk Flags" subtitle={`${structuredData.risk_flags.length} concern${structuredData.risk_flags.length > 1 ? "s" : ""} identified`} />
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {structuredData.risk_flags.map((f, i) => (
                          <FlagItem key={i} text={f} type="risk" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {structuredData.positive_signals?.length > 0 && (
                    <Box>
                      <SectionHeader icon={ThumbUpIcon} title="Positive Signals" subtitle={`${structuredData.positive_signals.length} strength${structuredData.positive_signals.length > 1 ? "s" : ""} identified`} />
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {structuredData.positive_signals.map((f, i) => (
                          <FlagItem key={i} text={f} type="positive" />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {structuredData.summary_narrative && (
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, backgroundColor: alpha(theme.palette.grey[500], 0.04) }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: theme.palette.text.primary, display: "flex", alignItems: "center", gap: 1 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          Executive Summary
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, fontSize: "0.9rem" }}>
                          {structuredData.summary_narrative}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {tab === "income" && structuredData.income && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionHeader icon={MonetizationOnIcon} title="Income Analysis" subtitle="Breakdown of verified and estimated income sources" />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={AccountBalanceWalletIcon} label="Verified Income" value={`KSh ${fmt(structuredData.income.verified_income)}`} subtext="Per month" color="success" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={TrendingUpIcon} label="Secondary Income" value={`KSh ${fmt(structuredData.income.secondary_income)}`} subtext="Unverified / informal" color="warning" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={AttachMoneyIcon} label="Monthly Average" value={`KSh ${fmt(structuredData.income.monthly_average)}`} subtext="Combined estimate" color="info" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={SpeedIcon} label="Regularity" value={structuredData.income.regularity || "—"} color="primary" />
                    </Grid>
                  </Grid>
                  <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <CategoryIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                        Primary Source
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                        {structuredData.income.primary_source || "No primary income source detected."}
                      </Typography>
                    </CardContent>
                  </Card>
                  {structuredData.income.income_dates?.length > 0 && (
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                          <CalendarTodayIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          Income Activity Calendar
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                          Days of the month when income transactions were detected
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
                            const hasIncome = structuredData.income.income_dates.includes(d);
                            return (
                              <Tooltip key={d} title={hasIncome ? "Income detected" : "No income"} arrow>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    backgroundColor: hasIncome ? theme.palette.primary.main : alpha(theme.palette.grey[500], 0.2),
                                    color: hasIncome ? theme.palette.common.white : theme.palette.text.disabled,
                                    transition: "all 0.2s",
                                    cursor: "default",
                                    "&:hover": {
                                      transform: hasIncome ? "scale(1.1)" : "none",
                                    },
                                  }}
                                >
                                  {d}
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              {tab === "spend" && structuredData.spending && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionHeader icon={ShoppingCartIcon} title="Spending Analysis" subtitle="Breakdown of monthly expenditure patterns" />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <MetricCard icon={ReceiptLongIcon} label="Total Monthly Average" value={`KSh ${fmt(structuredData.spending.total_monthly_average)}`} color="error" />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, height: "100%" }}>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                            <BarChartIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                            Top Spending Categories
                          </Typography>
                          <SpendBarChart categories={structuredData.spending.top_categories || []} />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, height: "100%" }}>
                        <CardContent sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <PaymentIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                            Fixed Monthly Obligations
                          </Typography>
                          {structuredData.spending.fixed_obligations?.length > 0 ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {structuredData.spending.fixed_obligations.map((o, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.grey[500], 0.08),
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                      {o.description}
                                    </Typography>
                                    <Chip label={o.frequency} size="small" sx={{ height: 20, fontSize: "0.7rem", mt: 0.5 }} />
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.error.main }}>
                                    KSh {fmt(o.amount)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                              No fixed obligations detected.
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tab === "debt" && structuredData.debt_profile && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionHeader icon={CreditCardIcon} title="Debt Profile" subtitle="Analysis of existing credit obligations and behavior" />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={PaymentIcon} label="Fuliza Usage" value={structuredData.debt_profile.fuliza_usage_count ?? 0} subtext="Times used" color={structuredData.debt_profile.fuliza_usage_count > 0 ? "warning" : "success"} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={AttachMoneyIcon} label="Fuliza Avg Amount" value={`KSh ${fmt(structuredData.debt_profile.fuliza_avg_amount)}`} color="warning" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={ReceiptLongIcon} label="Other Loan Repayments" value={`KSh ${fmt(structuredData.debt_profile.other_loan_repayments)}`} subtext="Per month" color="error" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MetricCard icon={SpeedIcon} label="Repayment Behavior" value={structuredData.debt_profile.fuliza_repayment_behavior || "N/A"} color="info" />
                    </Grid>
                  </Grid>
                  <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <CreditCardIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                        Digital Lenders Detected
                      </Typography>
                      {structuredData.debt_profile.digital_lenders_detected?.length === 0 ? (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
                          <Typography variant="body2" sx={{ color: theme.palette.success.dark, fontWeight: 500 }}>
                            None detected — no active mobile loan obligations
                          </Typography>
                        </Paper>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {structuredData.debt_profile.digital_lenders_detected.map((l, i) => (
                            <Paper
                              key={i}
                              elevation={0}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                              }}
                            >
                              <WarningAmberIcon sx={{ color: theme.palette.warning.main }} />
                              <Typography variant="body2" sx={{ color: theme.palette.warning.dark, fontWeight: 500 }}>
                                {l}
                              </Typography>
                            </Paper>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              )}

              {tab === "recommendation" && cr && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SectionHeader icon={MonetizationOnIcon} title="Credit Recommendation" subtitle="Loan options based on risk assessment" />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Card
                        elevation={0}
                        sx={{
                          border: `2px solid ${theme.palette.primary.main}`,
                          borderRadius: 3,
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        }}
                      >
                        <CardContent sx={{ p: 3, textAlign: "center" }}>
                          <SavingsIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            RECOMMENDED LIMIT
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main, my: 1 }}>
                            KSh {fmt(cr.recommended_loan_limit)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                        <CardContent sx={{ p: 3, textAlign: "center" }}>
                          <ShieldIcon sx={{ fontSize: 40, color: riskColor(cr.risk_level), mb: 1 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            RISK LEVEL
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: riskColor(cr.risk_level), my: 1, textTransform: "capitalize" }}>
                            {cr.risk_level}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3 }}>
                        <CardContent sx={{ p: 3, textAlign: "center" }}>
                          <PaymentIcon sx={{ fontSize: 40, color: theme.palette.text.primary, mb: 1 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                            MAX WEEKLY REPAYMENT
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary, my: 1 }}>
                            KSh {fmt(cr.max_weekly_repayment)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  {cr.installment_options?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <ReceiptLongIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                        Installment Options
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {cr.installment_options.map((opt, i) => (
                          <InstallmentCard key={i} opt={opt} index={i} isRecommended={i === 0} />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {cr.reasoning && (
                    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, backgroundColor: alpha(theme.palette.grey[500], 0.04) }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          Reasoning
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, fontSize: "0.9rem" }}>
                          {cr.reasoning}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}
            </>
          )}

          {/* RAW TEXT REPORT VIEW */}
          {!loading && !error && rawText && tab === "raw" && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                overflowX: "auto",
                fontSize: "0.875rem",
                lineHeight: 1.5,
                color: theme.palette.text.primary,
              }}
            >
              {rawText}
            </Paper>
          )}

          {!loading && !error && !structuredData && !rawText && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <InfoOutlinedIcon sx={{ color: theme.palette.warning.main }} />
              <Typography variant="body2" sx={{ color: theme.palette.warning.dark, fontWeight: 500 }}>
                No report data available for this statement.
              </Typography>
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default NarrativeReportPage;